import React from 'react';
import { useRouter } from 'next/router';
import { createColumnHelper } from '@tanstack/react-table';
import { Card, Table } from '@avila-tek/ui/src';
import { Hit } from '@algolia/client-search';
import { PageInfo, Pagination, User } from '../../models';
import { privilegesTranslate } from '../../static';
import { ActiveDotCell } from '../shared';
import { useAlgoliaSearch } from '../../hooks';
import { AlgoliaHitToObject } from '../../lib';

const columnHelper = createColumnHelper<User>();

const columns = [
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore library has a issue: https://github.com/TanStack/table/issues/4224
  columnHelper.accessor('firstName', {
    header: 'Nombre',
    cell: (info) =>
      `${info.row.original.firstName} ${info.row.original.lastName}`,
  }),

  columnHelper.accessor('privilege', {
    header: 'Privilegio',
    cell: (info) => privilegesTranslate[info?.getValue()],
  }),

  columnHelper.accessor('email', {
    header: 'Correo',
  }),

  columnHelper.accessor('active', {
    header: 'Estatus',
    cell: (info) => <ActiveDotCell active={info?.getValue()} />,
  }),
];

interface UserTableProps {
  data: Pagination<User>;
  pagination: Partial<PageInfo>;
  setPagination: React.Dispatch<React.SetStateAction<Partial<PageInfo>>>;
}

function UserTable({ data, pagination, setPagination }: UserTableProps) {
  const router = useRouter();

  const [searchText, setSearchText] = React.useState<string | number>('');
  const [usersData, setUsersData] = React.useState(data.items);
  const [pageInfoAlgolia, setPageInfoAlgolia] = React.useState<PageInfo>({
    currentPage: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    itemCount: 0,
    pageCount: 0,
    perPage: 10,
  });

  const { fetchData } = useAlgoliaSearch<User>({
    pageInfo: pageInfoAlgolia,
    setPageInfo: setPageInfoAlgolia,
    restrictSearchableAttributes: ['firstName', 'lastName', 'dni', 'email'],
    index: 'users',
    filters: `userType:admin`,
  });

  React.useEffect(() => {
    async function syncData() {
      if (searchText) {
        const { pageInfo, items } = await fetchData({
          searchText: searchText.toString(),
          newPage: pageInfoAlgolia.currentPage,
        });
        setUsersData(AlgoliaHitToObject<User>(items as Hit<User>[]));
        setPageInfoAlgolia(pageInfo);
      } else {
        setUsersData(data.items);
      }
    }
    syncData();
  }, [pageInfoAlgolia?.currentPage, searchText, pagination]);

  const onClickRow = (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    e.preventDefault();
    const { id } = e.currentTarget.dataset;
    router.push(`/app/users/${id}`);
  };

  const onClickAddButton = () => router.push('/app/users/create');

  const handleGoToPage = (value: number) => {
    if (searchText) {
      setPageInfoAlgolia({ ...pageInfoAlgolia, currentPage: value - 1 });
    } else {
      setPagination({ ...pagination, currentPage: value });
    }
  };

  return (
    <Card className="overflow-x-auto p-5">
      <Table<User>
        data={usersData}
        columns={columns}
        href="/app/users/create"
        manualPagination
      >
        <Table.Header className="p-6 w-full flex justify-between">
          <Table.SearchInput
            manualFilter
            value={searchText.toString()}
            onChange={(value) => {
              setSearchText(value);
            }}
          />
          <Table.AddButton title="Usuario" action={onClickAddButton} />
        </Table.Header>
        <Table.Content<User> onClickRow={onClickRow} />
        <Table.Footer>
          <Table.Pagination
            handleGoToPage={handleGoToPage}
            paginationInfo={{
              count: data.count,
              pageInfo: searchText
                ? {
                    ...pageInfoAlgolia,
                    currentPage: pageInfoAlgolia.currentPage + 1,
                  }
                : data.pageInfo,
            }}
            className="ml-auto"
          />
        </Table.Footer>
      </Table>
    </Card>
  );
}

export default UserTable;
